import cx from 'classnames';
import { InvoiceSummaryReportModel } from "../../api/models/invoiceSummaryReport";
import {
    providerAddress, providerCityStateZip,
    providerFaxNumber, providerFederalEin, providerName, providerPhoneNumber
} from '../../common/constants';


// Variables & general utility functions
//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
type InvoiceSummaryProps = {
    report: InvoiceSummaryReportModel | undefined,
    servicePeriod: string;
}

const currencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
});
//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~


// Main component
//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
const InvoiceSummaryReport = ({ report, servicePeriod }: InvoiceSummaryProps) => {
    return (
        <div id='summary-report'>
            <div id="summary-report-header" className="flex justify-between">
                <div id="summary-report-header-billing-section" className="w-1/3 text-left">
                    <p>Bill To:</p>
                    <h3 className="font-bold">{report?.districtName}</h3>
                    <h3 className="font-bold">{report?.districtAddress1}</h3>
                    <h3 className="font-bold">{report?.districtAddress2}</h3>
                    <h3 className="font-bold">{report?.districtCityStateZip}</h3>
                </div>
                <div id="summary-report-header-invoice-section" className="w-1/3 text-center">
                    <span className={
                        cx({
                            'text-xl font-bold': true,
                            'text-red-500': !report?.exportedOn,
                            'text-ptsgreen-normal': report?.exportedOn
                        })
                    }>
                        {report?.invoiceNumber
                            ? <h1>INVOICE #{report?.invoiceNumber}</h1>
                            : <h1>DRAFT INVOICE PREVIEW</h1>
                        }
                    </span>
                    <h3>Service Period: {servicePeriod}</h3>
                </div>
                <div id="summary-report-header-provider-section" className="w-1/3 text-right">
                    <h2 className="font-bold">{providerName}</h2>
                    <h2 className="font-bold">{providerFederalEin}</h2>
                    <h4>{providerAddress}</h4>
                    <h4>{providerCityStateZip}</h4>
                    <h4>Phone: {providerPhoneNumber} &nbsp; Fax: {providerFaxNumber}</h4>
                </div>
            </div>
            <hr className="my-2 border-2 border-black"></hr>
            {(!report || report.topLevelGroups?.length === 0) && (
                <div className="no-report-data">
                    <h2 className="text-center">No report data. Please check the District ID and service period.</h2>
                </div>
            )}
            {(report?.topLevelGroups && report.topLevelGroups.length > 0) && (
                <div className="my-5 report-body">
                    <div className='flex justify-between summary-header'>
                        {report.groupType && (
                            <div className='pl-2 font-bold underline summary-header-group w-96'>
                                <h3>{report.groupType}</h3>
                            </div>
                        )}
                        <div className='pl-2 font-bold underline summary-header-discipline w-60'>
                            <h3>Discipline</h3>
                        </div>
                        <div className='w-32 font-bold text-center underline summary-header-hours'>
                            <h3>Hours</h3>
                        </div>
                        <div className='w-32 font-bold text-center underline summary-header-billing-rate'>
                            <h3>Billing Rate</h3>
                        </div>
                        <div className='w-32 pr-2 font-bold text-right underline summary-header-amount'>
                            <h3>Amount</h3>
                        </div>
                    </div>
                    <div className='summary-body'>
                        {report?.topLevelGroups?.map((topLevelGroup, gIdx) => (
                            <div key={`topLevelSection_${gIdx}`} className="top-level-section odd:bg-gray-100">
                                <hr className="border-gray-300 border-1"></hr>
                                {topLevelGroup.disciplines?.map((discipline, dIdx) => (
                                    <div key={`disciplineSection_${dIdx}`}
                                        className="flex justify-between py-2 text-sm discipline-section hover:bg-ptsblue-normal hover:text-white">
                                        {report.groupType && (
                                            <h4 className="pl-2 top-level-group-name w-96">
                                                {dIdx === 0 ? topLevelGroup.topLevelGroupName : ''}
                                            </h4>
                                        )}
                                        <h4 className="pl-2 discipline-name w-60">{discipline.disciplineName}</h4>
                                        <h4 className='w-32 text-center discipline-hours'>{discipline.hours.toFixed(2)}</h4>
                                        <h4 className='w-32 text-center discipline-billing-rate'>{topLevelGroup.programId && topLevelGroup.programId > 0 ? (discipline.therapists && discipline.therapists.length && discipline.therapists[0].payrates && discipline.therapists[0].payrates.length > 0 ? discipline.therapists[0].payrates[0].rate?.toFixed(2) : discipline.billingRate.toFixed(2)) : discipline.billingRate.toFixed(2)}</h4>
                                        <h4 className='w-32 pr-2 text-right discipline-amount'>{currencyFormatter.format(discipline.amount)}</h4>
                                    </div>
                                ))}
                            </div>
                        ))}
                        <hr className="border-black border-1"></hr>
                        <h2 className="font-bold text-right total-amount-due">Total Amount Due: {currencyFormatter.format(report.totalAmountDue)}</h2>
                    </div>
                </div>
            )}
            <hr className="my-2 border-2 border-black"></hr>
            <div id="summary-report-footer" className="mt-5">
                <p>Please contact your billing specialist.</p>
            </div>
        </div>
    )
}
//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

export default InvoiceSummaryReport;
